import {
  FontWeight,
  getTypography,
  ThemedFunctor,
  TypographyName,
} from '@doveit/bricks';
import { css } from 'styled-components';

export const base: ThemedFunctor = ({
  theme, $isRemoving, $aspectRatio, $disabled,
}) => css`
  background: ${theme.color.background.neutral.default.low};
  position: relative;
  user-select: none;
  width: 100%;
  overflow: hidden;

  ${$disabled && `
    opacity: .5;
  `};

  aspect-ratio: ${$aspectRatio};

  ${$isRemoving && `
    opacity: .5;
  `}
`;

export const placeholder: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.color.background.neutral.hover.high}; // @TODO: review color palettes to add neutral.default.medium
`;

export const content: ThemedFunctor = ({ theme, $hasPicture }) => css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  ${$hasPicture && `
    background: ${theme.color.background.neutral.default.low};
  `}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const skeletonContent: ThemedFunctor = () => css`
  position: absolute;
  inset: 0;

  & > * {
    height: 100%;
  }
`;

export const frame: ThemedFunctor = ({
  theme,
  $operation,
  $interactive,
  $active,
  $hasError,
  $hasWarning,
}) => css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease-in-out;
  z-index: 2;
  border: 2px solid ${theme.color.background.neutral.default.high};

  ${$operation === 'drag' && `
    cursor: move;
  `}

  ${$interactive && `
    cursor: pointer;

    &:hover {
      border-color: ${theme.color.background.neutral.hover.high};

      .index {
        background-color: ${theme.color.background.neutral.hover.high};
        color: ${theme.color.foreground.neutral.default.low};
      }

      .icon {
        visibility: visible;
        opacity: 1;
      }
    }
  `}

  ${$hasError && `
    border-color: ${theme.color.background.critical.default.high};

    .index {
      background-color: ${theme.color.background.critical.default.high};
      color: ${theme.color.foreground.critical.default.low};
    }
  `};

  ${$hasWarning && !$hasError && `
    border-color: ${theme.color.background.warning.default.high};

    .index {
      background-color: ${theme.color.background.warning.default.high};
      color: ${theme.color.foreground.warning.default.low};
    }
  `};

   ${$active && `
    &,
    &:hover {
      border-color: ${theme.color.background.primary.default.high};

      .index {
        background-color: ${theme.color.background.primary.default.high};
        color: ${theme.color.foreground.primary.default.low};
      }
    }
  `}
`;

export const activeIcon: ThemedFunctor = ({ theme, $disabled }) => css`
  position: absolute;
  left: ${theme.remSize[50]};
  top: ${theme.remSize[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.remSize[250]};
  height: ${theme.remSize[250]};
  border-radius: ${theme.remSize[250]};
  background-color: ${theme.color.background.primary.default.high};
  color: ${theme.color.foreground.primary.default.low};

  ${$disabled && `
    background-color: ${theme.color.background.neutral.default.high};
    color: ${theme.color.foreground.neutral.default.low};
  `};
`;

export const alertIcon: ThemedFunctor = ({ theme, $disabled, $hasError }) => css`
  position: absolute;
  left: ${theme.remSize[50]};
  bottom: ${theme.remSize[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.remSize[250]};
  height: ${theme.remSize[250]};
  border-radius: ${theme.remSize[250]};
  background-color: ${theme.color.background[$hasError ? 'critical' : 'warning'].default.high};
  color: ${theme.color.foreground[$hasError ? 'critical' : 'warning'].default.low};

  ${$disabled && `
    background-color: ${theme.color.background.neutral.default.high};
    color: ${theme.color.foreground.neutral.default.low};
  `};
`;

export const badge: ThemedFunctor = ({ theme, $hasAlertIcon }) => css`
  position: absolute;
  bottom: ${theme.remSize[50]};
  /* Placed after the alert icon (when shown) and before the index */
  left: ${$hasAlertIcon ? `calc(${theme.remSize[50]} * 2 + ${theme.remSize[250]})` : theme.remSize[50]};
  right: calc(${theme.remSize[250]} + 2px + ${theme.remSize[50]});
  display: flex;
  min-width: 0;
  pointer-events: none;

  /* Badge does not truncate its label: constrain it and ellipsize the inner text */
  & > * {
    max-width: 100%;
    min-width: 0;
  }

  & > * > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const icon: ThemedFunctor = ({ theme, $active }) => css`
  visibility: hidden;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.remSize[300]};
  height: ${theme.remSize[300]};
  border-radius: ${theme.remSize[150]};
  background-color: ${theme.color.background[$active ? 'primary' : 'neutral'].default.high};
  color: ${theme.color.foreground[$active ? 'primary' : 'neutral'].default.low};
  transition: opacity 0.2s ease-in-out;
`;

export const index: ThemedFunctor = ({ theme }) => css`
  ${getTypography(theme, TypographyName.CAPTION, { fontWeight: FontWeight.MEDIUM })}
  background: ${theme.color.background.neutral.default.high};
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: calc(${theme.remSize[250]} + 2px);
  height: calc(${theme.remSize[250]} + 2px);
  padding: 0 ${theme.remSize[25]};
    border-radius: ${theme.remSize[50]} 0 0 0;

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
`;

export const actions: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  top: ${theme.remSize[50]};
  right: ${theme.remSize[50]};
  display: flex;
  align-items: center;
  gap: ${theme.remSize[75]};
  z-index: 1;
`;
